import {
  Component,
  EventEmitter,
  inject,
  Input,
  type OnDestroy,
  Output,
  signal,
  ViewChild,
  OnInit,
  DestroyRef,
  Renderer2,
  AfterViewInit,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { CInputTextComponent, ToastState } from "@cariloop/ui-components";
import type { MfaType } from "app/access/login/mfa-login/mfa-type.type";
import {
  type LoginCredentials,
  MfaService,
} from "app/shared/services/mfa.service";
import { ToastProperties } from "app/shared/services/toast.service";
import type { Subscription } from "rxjs";
import { AppState } from "../../../app.service";
import { AccountService } from "../../../shared/services/account.service";
import { ResendMfaEmailService } from "./resend-mfa-email.service";
import { ErrorReportingService } from "../../../shared/services/error-reporting.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Title } from "@angular/platform-browser";
import { TranslateService } from "@ngx-translate/core";
import { HttpErrorResponse } from "@angular/common/http";

const MFA_LIMIT_MS = 300_000;

function getNewMfaLimit() {
  return new Date(new Date().getTime() + MFA_LIMIT_MS);
}

@Component({
  selector: "app-mfa-login",
  templateUrl: "./mfa-login.component.html",
  styleUrls: ["./mfa-login.component.scss"],
  providers: [MfaService, ResendMfaEmailService],
  standalone: false,
})
export class MfaLoginComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild("mfaForm", { static: false }) mfaCodeForm: NgForm;

  @Input({ required: true })
  loginCredentials: LoginCredentials;

  readonly #mfaTokenSignal = signal("");
  get mfaToken() {
    return this.#mfaTokenSignal();
  }
  @Input({ required: true })
  set mfaToken(value: string) {
    this.#mfaTokenSignal.set(value);
  }

  @Input({ required: true })
  mfaType: MfaType;

  @Output() backToLogin = new EventEmitter<string>();
  @Output() mfaSuccess = new EventEmitter<any>();
  public error: HttpErrorResponse & { message?: string; action?: string };
  public message: any[] = [];
  loginMessage = "";
  mfaCode = "";

  mfaTrustDevice = false;
  mfaError = 0;
  mfaErrorLabel = "MFA.NO_CODE_ERROR";
  screen: "mfa" | "mfaError" = "mfa";
  toastProperties: ToastProperties = {
    toastMessage: "MFA.EMAIL.RESEND.TOAST",
    showToast: false,
    toastType: ToastState.success,
  };

  readonly #resendMfaEmailService = inject(ResendMfaEmailService);
  readonly #resendMfaEmailSubscription: null | Subscription = null;

  protected readonly mfaLimit = signal(getNewMfaLimit());
  #resetMfaLimit() {
    this.mfaLimit.set(getNewMfaLimit());
  }

  constructor(
    private appService: AppState,
    private accountService: AccountService,
    private errorReportingService: ErrorReportingService,
    private titleService: Title,
    private translateService: TranslateService,
    private destroyRef: DestroyRef,
    private renderer: Renderer2,
  ) {
    this.#resendMfaEmailSubscription =
      this.#resendMfaEmailService.resendCode$.subscribe();
  }

  ngOnInit(): void {
    if (this.screen === "mfa" || this.screen === "mfaError") {
      this.translateService
        .stream("MFA.PAGE_TITLE")
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((pageTitle) => {
          this.titleService.setTitle(pageTitle + " | Cariloop");
        });
    }
  }

  ngAfterViewInit(): void {
    this.renderer.listen(
      this.renderer.selectRootElement("input[name='verificationCode']"),
      "input",
      (event: InputEvent) => {
        const verificationCodeControl =
          this.mfaCodeForm.form.controls.verificationCode;
        const cleanValue = (event.target as HTMLInputElement).value.replace(
          /[^0-9]/g,
          "",
        );
        verificationCodeControl.setValue(cleanValue);
        verificationCodeControl.markAsUntouched();
      },
    );
  }

  onSubmitMfa(verificationInput: { valueAccessor: CInputTextComponent }) {
    verificationInput.valueAccessor.textInputRef.nativeElement.blur();

    if (this.mfaCodeForm.invalid) {
      verificationInput.valueAccessor.textInputRef.nativeElement.focus();
      return;
    }

    this.accountService
      .mfaLogin({
        code: this.mfaCode,
        mfaToken: this.#mfaTokenSignal(),
        trustDevice: this.mfaTrustDevice,
      })
      .subscribe(
        (res) => {
          this.mfaSuccess.emit(res);
        },
        (err) => {
          this.error = null;

          this.mfaError++;
          this.mfaCode = "";
          if (this.mfaError > 2) {
            this.mfaErrorLabel = "MFA.LOGIN_ERROR";
            this.screen = "mfaError";
            return;
          }
          this.error = err;
          this.mfaCodeForm.resetForm({ mfaTrustDevice: this.mfaTrustDevice });
          if (err.status === 422) {
            this.error.message = "REGISTER.VERIFY_EMAIL.ERRORS.INVALID_CODE";
            this.error.action = "";
          } else {
            this.error.message = "MFA.UNEXPECTED_ERROR.MESSAGE";
            this.error.action = "MFA.UNEXPECTED_ERROR.ACTION";
          }
          if (
            err.status &&
            (err.status === 401 ||
              err.status === 441 ||
              err.status === 461 ||
              err.status === 422)
          ) {
            return;
          }
          this.errorHandler(err);
        },
      );
  }

  private errorHandler(error, custom?: any): void {
    this.appService.status(error);
    const errObj = { error: this.appService.formatError(error) };
    if (error.status !== 451) {
      this.errorReportingService.send(error, custom);
    }
  }

  goBackToLogin(value: string) {
    this.backToLogin.emit(value);
  }

  #resendCodeThroughSMS() {
    this.accountService.resendMfaCode(this.#mfaTokenSignal()).subscribe(
      () => {},
      (err) => {
        this.error = null;
        this.mfaError++;
        if (
          err?.error?.error?.code === "MFA_CODE_SENT_SMS" ||
          err?.error?.error?.code === "MFA_CODE_SENT_EMAIL"
        ) {
          this.#resetMfaLimit();
          this.mfaCode = "";
          this.#mfaTokenSignal.set(err?.error?.error?.name);
          this.screen = "mfa";
          return;
        }
        this.error = err;
        if (
          err.status &&
          (err.status === 401 || err.status === 441 || err.status === 461)
        ) {
          return;
        }
        this.errorHandler(err);
      },
    );
  }

  requestNewCode() {
    if (this.mfaError > 2) {
      this.mfaErrorLabel = "MFA.LOGIN_ERROR";
      this.screen = "mfaError";
      return;
    }
    this.toastProperties.showToast = true;
    setTimeout(() => {
      this.toastProperties.showToast = false;
    }, 3000);

    if (this.mfaType === "sms") {
      this.#resendCodeThroughSMS();
    }

    if (this.mfaType === "email") {
      this.#resendMfaEmailService.handleResendCode({
        loginCredentials: this.loginCredentials,
        mfaType: this.mfaType,
        onResendMfaEmailSuccess: (newAccountMfaCodeToken: string) => {
          this.#resetMfaLimit();

          this.#mfaTokenSignal.set(newAccountMfaCodeToken);
        },
      });
    }
  }

  ngOnDestroy() {
    this.#resendMfaEmailSubscription?.unsubscribe();
    this.titleService.setTitle("Cariloop");
  }
}
